import React, { useEffect, useState } from 'react';
import EditMailForm from './EditMailForm';
import NewMailPage from './NewMailPage';
import SendEmailDialog from './SendEmailDialog';

interface Email {
  id: number;
  subject: string;
  body: string;
  created_at: string;
  status?: string;
}

interface MailPageProps {
  accessToken?: string;
  userRoles?: string[];
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

const MailPage: React.FC<MailPageProps> = ({ accessToken, userRoles }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMailId, setEditingMailId] = useState<number | null>(null);
  const [showNewMail, setShowNewMail] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<{ id: number; subject: string } | null>(null);
  const [sendFeedback, setSendFeedback] = useState<{ success: boolean; message: string } | null>(null);

  // Determine if user can send emails
  const isAdmin = hasRole(userRoles, 'admin');
  const isTrustee = hasRole(userRoles, 'trustee');
  const isSportsAdmin = getUserSports(userRoles).length > 0;
  const canSend = isAdmin || isTrustee || isSportsAdmin;
  // Only admins can manage emails (create/edit/delete drafts)
  const canManage = isAdmin;

  const fetchEmails = () => {
    setLoading(true);
    fetch('/api/email/list', {
      headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : undefined
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setEmails(data);
          setError(null);
        } else {
          setEmails([]);
          setError(data.error || 'Failed to load emails.');
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError('Network error');
      });
  };

  const handleSend = async (emailId: number, sportId: number) => {
    setSendDialogOpen(false);
    setSelectedEmail(null);
    setSendFeedback(null);
    try {
      const res = await fetch(`/api/email/${emailId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ sportId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSendFeedback({
          success: true,
          message: `Email sent to ${data.sent} member${data.sent !== 1 ? 's' : ''}.${data.failed > 0 ? ` ${data.failed} failed.` : ''}`
        });
        fetchEmails();
      } else {
        setSendFeedback({ success: false, message: data.error || 'Failed to send email.' });
      }
    } catch (err) {
      setSendFeedback({ success: false, message: 'Network error while sending email.' });
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  // Admin-only features
  const showManageButtons = canManage;

  return (
    <div className="relative">
      <SendEmailDialog
        open={sendDialogOpen}
        email={selectedEmail}
        onClose={() => { setSendDialogOpen(false); setSelectedEmail(null); }}
        onSend={handleSend}
        onQuickSend={() => {}}
        apiBaseUrl="/api"
        accessToken={accessToken}
        userRoles={userRoles}
      />
      {sendFeedback && (
        <div className={`mb-4 px-4 py-3 rounded font-semibold ${sendFeedback.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {sendFeedback.message}
          <button className="ml-4 text-sm underline" onClick={() => setSendFeedback(null)}>Dismiss</button>
        </div>
      )}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <h1 className="text-2xl font-bold">Mail</h1>
        <div className="flex gap-2">
          {/* Quick Send for admin/trustee/sports admin */}
          {canSend && (
            <button
              className="bg-green-600 hover:bg-green-800 text-white font-bold py-2 px-6 rounded-md shadow-lg transition duration-300 ease-in-out"
              onClick={() => { setSelectedEmail(null); setSendDialogOpen(true); }}
            >
              Quick Send
            </button>
          )}
          {/* Create draft - admin only */}
          {canManage && (
            <button
              className="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-6 rounded-md shadow-lg transition duration-300 ease-in-out"
              onClick={() => setShowNewMail(true)}
            >
              New Mail
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        {showNewMail ? (
          <NewMailPage accessToken={accessToken} onSuccess={() => { setShowNewMail(false); fetchEmails(); }} onCancel={() => setShowNewMail(false)} />
        ) : editingMailId ? (
          <EditMailForm
            emailId={editingMailId}
            accessToken={accessToken}
            onClose={() => setEditingMailId(null)}
            onSaved={fetchEmails}
          />
        ) : loading ? (
          <div className="text-center py-8">Loading mail...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">Error: {error}</div>
        ) : (
          <table className="min-w-full bg-white border border-gray-200 mb-6 rounded-lg shadow">
            <thead className="bg-gray-200">
              <tr>
                <th className="py-2 px-4 text-left">Subject</th>
                <th className="py-2 px-4 text-left">Created At</th>
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {emails.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-4 px-4 text-center text-gray-400">
                    {canSend
                      ? 'No mail messages found. Use Quick Send to email your members directly.'
                      : 'No mail messages found.'}
                  </td>
                </tr>
              ) : (
                emails.map(email => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="py-2 px-4">{email.subject}</td>
                    <td className="py-2 px-4">{new Date(email.created_at).toLocaleString()}</td>
                    <td className="py-2 px-4 flex gap-2">
                      {/* Edit - admin only */}
                      {showManageButtons && (
                        <button
                          className="text-green-600 hover:underline"
                          onClick={() => setEditingMailId(email.id)}
                        >
                          Edit
                        </button>
                      )}
                      {/* Send - for anyone with email permission */}
                      {canSend && (
                        <button
                          className="text-blue-600 hover:underline"
                          onClick={() => { setSelectedEmail({ id: email.id, subject: email.subject }); setSendDialogOpen(true); }}
                        >
                          Send
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MailPage;
